import { MeetingType } from "@/meetings/meetingsDomain";
import { TMessage } from "@/i18n/tt";
import { IconName } from "@/components-v2/iconMapping";

export enum ArchiveDocumentType {
  BOARD_MEETING = "board_meeting",
  NOTICE_GENERAL_MEETING = "notice_general_meeting",
  GENERAL_MEETING = "general_meeting",
  NOTICE_BOARD_MEETING = "notice_board_meeting",
  ARTICLESOFASSOCIATION = "articlesofassociation",
  SUBSCRIPTION_FORM = "subscription_form",
  FILE = "uploaded_file",
  FOLDER = "folder",
  BOARD_ARCHIVE_FOLDER = "board_archive_folder",
  BOARD_MEETINGS_FOLDER = "board_meetings_folder",
  GENERAL_MEETINGS_FOLDER = "general_meetings_folder",
  SHAREHOLDER_ARCHIVE_FOLDER = "shareholder_archive_folder",
  ADMIN_ARCHIVE_FOLDER = "admin_archive_folder",
  CUSTOM_ARCHIVE_FOLDER = "custom_archive_folder",
  LINK = "link",
  ATTACHMENT = "attachment",
}

export enum ArchiveDocumentStatus {
  CREATED = "CREATED",
  ARCHIVED = "ARCHIVED",
  DELETED = "DELETED",
}

export enum ArchiveRootFolderType {
  BOARD_ARCHIVE = "board-archive",
  GENERAL_MEETINGS = "general-meetings",
  SEND_FOR_SIGNING = "send-for-signing",
  ADMIN_ARCHIVE = "admin-archive",
  SHAREHOLDER_ARCHIVE = "shareholder-archive",
  CUSTOM_ARCHIVE = "custom-archive",
  RECENT = "recent",
  STARRED = "starred",
}

const archiveRootFolderViews = ["files", "deleted", "permissions", "settings"] as const;

export type ArchiveRootFolderView = (typeof archiveRootFolderViews)[number];

export function isArchiveRootFolderView(s: string | undefined): s is ArchiveRootFolderView {
  return archiveRootFolderViews.includes(s as ArchiveRootFolderView);
}

export type ArchivePathNode = {
  id: string;
  name: string;
  type: ArchiveDocumentType;
  projectId?: string;
  meetingType?: MeetingType;
  meetingDate?: string;
  folderDescription?: string;
  folderIcon?: IconName;
  folderIconColor?: string;
};

export type Breadcrumb = { id: string; name: TMessage };

export type ArchiveEntryAction =
  | "delete-signing-request"
  | "delete"
  | "delete-forever"
  | "download"
  | "edit-in-dbot"
  | "move"
  | "open"
  | "open-in-archive"
  | "open-meeting"
  | "preview"
  | "rename"
  | "restore"
  | "see-signing-status"
  | "send-for-signing";

export function getActionIcon(action: ArchiveEntryAction): IconName {
  switch (action) {
    case "delete-signing-request":
      return "x-square";
    case "delete":
    case "delete-forever":
      return "trash";
    case "download":
      return "download";
    case "edit-in-dbot":
      return "edit";
    case "move":
      return "arrow-square-right";
    case "open":
    case "open-in-archive":
      return "folder-open";
    case "open-meeting":
    case "preview":
      return "eye";
    case "rename":
      return "edit";
    case "see-signing-status":
      return "hourglass";
    case "send-for-signing":
      return "paper-plane";
    case "restore":
      return "clock-clockwise";
  }
}

export function getActionLabel(action: ArchiveEntryAction): TMessage {
  switch (action) {
    case "delete-signing-request":
      return { key: "archive.delete-signing-request" };
    case "delete":
      return { key: "archive.move-to-trash" };
    case "delete-forever":
      return { key: "archive.delete-forever" };
    case "download":
      return { key: "download" };
    case "edit-in-dbot":
      return { key: "archive.edit-in-dbot" };
    case "move":
      return { key: "archive.move" };
    case "open":
    case "open-meeting":
      return { key: "open" };
    case "open-in-archive":
      return { key: "archive.open-in-archive" };
    case "preview":
      return { key: "preview" };
    case "rename":
      return { key: "rename" };
    case "restore":
      return { key: "archive.restore" };
    case "see-signing-status":
      return { key: "archive.signing-status" };
    case "send-for-signing":
      return { key: "archive.send-for-signing" };
  }
}
